import { InsuranceActionListenerCallback } from './InsuranceActionListener'
import {
  CommerceBenefitPlanResponse,
  CommerceInsuranceDataResponse,
  CommerceProductQuoteRequestItem,
  CommerceProductQuoteResponse,
  ServiceStatusCallback,
  ServiceType,
} from './Service'

export const CUSTOM_EVENT_NAME = {
  APP: 'db:insuranceEvent',
  DRAWER: 'db:insuranceDrawerEvent',
  WIDGET: 'db:insuranceWidgetEvent',
} as const

/**
 * Custom events from commerce app
 */
export type InsuranceEventType =
  | 'onLogin'
  | 'onLogout'
  | 'onSessionError'
  | 'onOrderComplete'
  | 'onCartBundleChange'
  | 'onCartAddItem'
  | 'onCartRemoveItem'
  | 'onCartEditItem'
  | 'onCartChangeQuantity'
  | 'onCartApplySubscription'
  | 'onCartApplyPromoCode'
  | 'onCartRemovePromoCode'
  | 'onServiceStatus'
  | 'onRequestProductQuote'
  | 'onRequestBenefitPlan'
  | 'onAddSyncInsuranceListener'
  | 'onRemoveSyncInsuranceListener'
  | 'onAddInsuranceActionListener'
  | 'onRemoveInsuranceActionListener'
  | 'onAddInsuranceBenefits'
  | 'onRemoveInsuranceBenefits'
  | 'onRequestInsuranceData'

export type DrawerEventType = 'onOpenDrawer'

export type VoidCallback = () => void

export type SessionError = {
  errorCode?: string
  errorKey?: string
}

export type LoginEvent = {
  type: 'onLogin'
}

export type LogoutEvent = {
  type: 'onLogout'
}

export type OpenDrawerEventPayload = {
  target?: string
  navigateState?: object
  viewOnly?: boolean
  callback?: () => void
}

export type OpenDrawerEvent = {
  type: 'onOpenDrawer'
  payload?: OpenDrawerEventPayload
}

export type SessionErrorEvent = {
  type: 'onSessionError'
  payload?: SessionError
}

export type OrderCompleteEvent = {
  type: 'onOrderComplete'
}

export type CartBundleChangeEvent = {
  type: 'onCartBundleChange'
}

/**
 * @deprecated
 */
export type CartAddItemEvent = {
  type: 'onCartAddItem'
  payload: VoidCallback
}

/**
 * @deprecated
 */
export type CartRemoveItemEvent = {
  type: 'onCartRemoveItem'
  payload: VoidCallback
}

/**
 * @deprecated
 */
export type CartChangeQuantityEvent = {
  type: 'onCartChangeQuantity'
  payload: VoidCallback
}

/**
 * @deprecated
 */
export type CartEditItemEvent = {
  type: 'onCartEditItem'
  payload: VoidCallback
}

/**
 * @deprecated
 */
export type CartApplySubscriptionEvent = {
  type: 'onCartApplySubscription'
  payload: VoidCallback
}

export type CartApplyPromoCodeEvent = {
  type: 'onCartApplyPromoCode'
  payload: VoidCallback
}

/**
 * @deprecated
 */
export type CartRemovePromoCodeEvent = {
  type: 'onCartRemovePromoCode'
  payload: VoidCallback
}

export type ServiceStatusEvent = {
  type: 'onServiceStatus'
  payload: {
    serviceType: ServiceType
    handler: ServiceStatusCallback
  }
}

export type RequestProductQuoteEvent = {
  type: 'onRequestProductQuote'
  payload: {
    request: CommerceProductQuoteRequestItem[]
    handler: (response: CommerceProductQuoteResponse) => void
  }
}

// NOTE: for backwards compatibility
/**
 * @deprecated
 */
export type LegacyAddInsuranceListenerEvent = {
  type: 'onAddSyncInsuranceListener'
  payload?: InsuranceActionListenerCallback
}

// NOTE: for backwards compatibility
/**
 * @deprecated
 */
export type LegacyRemoveInsuranceListenerEvent = {
  type: 'onRemoveSyncInsuranceListener'
  payload?: InsuranceActionListenerCallback
}

export type AddInsuranceActionListenerEvent = {
  type: 'onAddInsuranceActionListener'
  payload?: InsuranceActionListenerCallback
}

export type RemoveInsuranceActionListenerEvent = {
  type: 'onRemoveInsuranceActionListener'
  payload?: InsuranceActionListenerCallback
}

export type AddInsuranceBenefitsEvent = {
  type: 'onAddInsuranceBenefits'
}

export type RemoveInsuranceBenefitsEvent = {
  type: 'onRemoveInsuranceBenefits'
}

export type RequestBenefitPlanEvent = {
  type: 'onRequestBenefitPlan'
  payload: (response: CommerceBenefitPlanResponse) => void
}

export type RequestInsuranceDataEvent = {
  type: 'onRequestInsuranceData'
  payload: (response: CommerceInsuranceDataResponse) => void
}

export type InsuranceEvent =
  | ServiceStatusEvent
  | LoginEvent
  | LogoutEvent
  | SessionErrorEvent
  | OrderCompleteEvent
  | CartBundleChangeEvent
  | CartAddItemEvent
  | CartRemoveItemEvent
  | CartEditItemEvent
  | CartChangeQuantityEvent
  | CartApplySubscriptionEvent
  | CartApplyPromoCodeEvent
  | CartRemovePromoCodeEvent
  | ServiceStatusEvent
  | RequestProductQuoteEvent
  | LegacyAddInsuranceListenerEvent
  | LegacyRemoveInsuranceListenerEvent
  | AddInsuranceActionListenerEvent
  | RemoveInsuranceActionListenerEvent
  | AddInsuranceBenefitsEvent
  | RemoveInsuranceBenefitsEvent
  | RequestBenefitPlanEvent
  | RequestInsuranceDataEvent

export type DrawerEvent = OpenDrawerEvent
